import React, { useState, useEffect } from 'react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Users,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  UserPlus,
  RefreshCw,
  Calendar,
  Eye,
} from 'lucide-react';
import api from '../utils/axios';

const formatCurrency = (value?: number) =>
  `$${(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const formatNumber = (value?: number) =>
  Number(value ?? 0).toFixed(2);


const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const getDashboardData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/getDashboardData');
      const data = response.data;
      setDashboardData(data?.data ?? {});
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-gray-50">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header Skeleton */}
          <div className="h-32 bg-white rounded-2xl animate-pulse shadow-sm" />
          
          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="h-40 bg-white rounded-2xl animate-pulse shadow-sm"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-600 to-gray-600 bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
              <p className="text-slate-500 mt-1">
                Real-time overview of platform metrics and performance
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-4 py-2 rounded-lg">
                <Calendar size={16} />
                <span>
                  Updated: {lastUpdated.toLocaleTimeString()}
                </span>
              </div>
              <button
                onClick={getDashboardData}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md hover:shadow-lg"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Wallet Balances Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <BalanceCard
            title="Total Client Wallet Balance"
            value={formatCurrency(dashboardData?.totalClientWalletBalance)}
            change="+20.1% from last month"
            changeType="positive"
            gradient="from-green-500 to-emerald-600"
            icon={<Wallet size={24} />}
          />
          <BalanceCard
            title="Total Agent Wallet Balance"
            value={formatCurrency(dashboardData?.totalAgentWalletBalance)}
            change="+15% from last month"
            changeType="positive"
            gradient="from-blue-500 to-cyan-600"
            icon={<Wallet size={24} />}
          />
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Users"
            value={dashboardData?.totalUsers?.toLocaleString() ?? '0'}
            subtitle="+12% from last month"
            icon={<Users size={20} />}
            gradient="from-gray-500 to-pink-600"
            trend="up"
          />

          <MetricCard
            title="Total Deposits"
            value={formatCurrency(dashboardData?.totalDeposits)}
            subtitle="+20.1% from last month"
            icon={<ArrowUpCircle size={20} />}
            gradient="from-blue-500 to-gray-600"
            trend="up"
          />

          <MetricCard
            title="Total Withdrawals"
            value={formatCurrency(dashboardData?.totalWithdrawals)}
            subtitle="-5% from last month"
            icon={<ArrowDownCircle size={20} />}
            gradient="from-red-500 to-rose-600"
            trend="down"
          />

          <MetricCard
            title="Net Revenue"
            value={formatCurrency(
              (dashboardData?.totalDeposits ?? 0) - 
              (dashboardData?.totalWithdrawals ?? 0)
            )}
            subtitle="Deposits - Withdrawals"
            icon={<TrendingUp size={20} />}
            gradient="from-amber-500 to-orange-600"
            trend="up"
          />
        </div>

        {/* Transaction Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatusCard
            title="Deposits Overview"
            gradient="from-green-500 to-emerald-600"
            stats={[
              {
                label: 'Approved',
                count: dashboardData?.deposits?.approvedCount,
                amount: dashboardData?.deposits?.approvedAmount,
              },
              {
                label: 'Pending',
                count: dashboardData?.deposits?.pendingCount,
                amount: dashboardData?.deposits?.pendingAmount,
              },
              {
                label: 'Rejected',
                count: dashboardData?.deposits?.rejectedCount,
                amount: dashboardData?.deposits?.rejectedAmount,
              },
            ]}
          />

          <StatusCard
            title="Withdrawals Overview"
            gradient="from-red-500 to-rose-600"
            stats={[
              {
                label: 'Approved',
                count: dashboardData?.withdrawals?.approvedCount,
                amount: dashboardData?.withdrawals?.approvedAmount,
              },
              {
                label: 'Pending',
                count: dashboardData?.withdrawals?.pendingCount,
                amount: dashboardData?.withdrawals?.pendingAmount,
              },
              {
                label: 'Rejected',
                count: dashboardData?.withdrawals?.rejectedCount,
                amount: dashboardData?.withdrawals?.rejectedAmount,
              },
            ]}
          />
        </div>


        {/* Today's Activity Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center">
              <Activity className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                Today's Activity
              </h2>
              <p className="text-sm text-slate-500">
                Real-time metrics for today
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <TodayStatCard
              title="Today's Deposits"
              value={formatCurrency(dashboardData?.todayDeposits)}
              icon={<ArrowUpCircle size={18} />}
              color="green"
            />

            <TodayStatCard
              title="Today's Withdrawals"
              value={formatCurrency(dashboardData?.todayWithdrawals)}
              icon={<ArrowDownCircle size={18} />}
              color="red"
            />

            <TodayStatCard
              title="New Users Today"
              value={dashboardData?.todayUsers ?? 0}
              icon={<UserPlus size={18} />}
              color="blue"
            />

            <TodayStatCard
              title="Today's Net"
              value={formatCurrency(
                (dashboardData?.todayDeposits ?? 0) - 
                (dashboardData?.todayWithdrawals ?? 0)
              )}
              icon={<DollarSign size={18} />}
              color="gray"
            />
          </div>
        </div>

        {/* Quick Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SummaryCard
            title="Avg. Deposit per User"
            value={formatCurrency(
              (dashboardData?.totalDeposits ?? 0) / 
              (dashboardData?.totalUsers || 1)
            )}
            icon={<TrendingUp size={20} />}
          />
          <SummaryCard
            title="Avg. Withdrawal per User"
            value={formatCurrency(
              (dashboardData?.totalWithdrawals ?? 0) / 
              (dashboardData?.totalUsers || 1)
            )}
            icon={<TrendingDown size={20} />}
          />
          <SummaryCard
            title="Total Platform Balance"
            value={formatCurrency(
              (dashboardData?.totalClientWalletBalance ?? 0) + 
              (dashboardData?.totalAgentWalletBalance ?? 0)
            )}
            icon={<Wallet size={20} />}
          />
        </div>
      </div>
    </div>
  );
};

/* ================= COMPONENTS ================= */

const BalanceCard = ({
  title,
  value,
  change,
  changeType,
  gradient,
  icon,
}: {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  gradient: string;
  icon: React.ReactNode;
}) => (
  <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
    <div className={`bg-gradient-to-br ${gradient} p-6`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-2">{title}</p>
          <p className="text-4xl font-bold text-white">{value}</p>
        </div>
        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white">
          {icon}
        </div>
      </div>
    </div>
    <div className="p-4 bg-slate-50">
      <div className="flex items-center gap-2">
        {changeType === 'positive' ? (
          <TrendingUp size={16} className="text-green-600" />
        ) : (
          <TrendingDown size={16} className="text-red-600" />
        )}
        <span
          className={`text-sm font-medium ${
            changeType === 'positive' ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {change}
        </span>
      </div>
    </div>
  </div>
);

const MetricCard = ({
  title,
  value,
  subtitle,
  icon,
  gradient,
  trend,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  gradient: string;
  trend?: 'up' | 'down';
}) => (
  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 hover:shadow-lg transition-all">
    <div className="flex items-center justify-between mb-4">
      <div
        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-md`}
      >
        {icon}
      </div>
      {trend && (
        <div
          className={`px-2 py-1 rounded-lg ${
            trend === 'up' ? 'bg-green-100' : 'bg-red-100'
          }`}
        >
          {trend === 'up' ? (
            <TrendingUp size={14} className="text-green-600" />
          ) : (
            <TrendingDown size={14} className="text-red-600" />
          )}
        </div>
      )}
    </div>
    <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">
      {title}
    </p>
    <p className="text-2xl font-bold text-slate-800 mb-1">{value}</p>
    <p className="text-xs text-slate-500">{subtitle}</p>
  </div>
);

const TodayStatCard = ({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'green' | 'red' | 'blue' | 'gray';
}) => {
  const colorClasses = {
    green: 'from-green-500 to-emerald-600',
    red: 'from-red-500 to-rose-600',
    blue: 'from-blue-500 to-gray-600',
    gray: 'from-gray-500 to-pink-600',
  };

  return (
    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
      <div className="flex items-center gap-3 mb-3">
        <div
          className={`w-9 h-9 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center text-white shadow-sm`}
        >
          {icon}
        </div>
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          {title}
        </p>
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
    </div>
  );
};

const SummaryCard = ({
  title,
  value,
  icon,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
}) => (
  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 hover:shadow-lg transition-all">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
        {icon}
      </div>
      <p className="text-sm font-medium text-slate-600">{title}</p>
    </div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
  </div>
);

const StatusCard = ({
  title,
  gradient,
  stats,
}: {
  title: string;
  gradient: string;
  stats: {
    label: string;
    count: number;
    amount: number;
  }[];
}) => (
  <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
    <div className={`bg-gradient-to-br ${gradient} p-5`}>
      <h3 className="text-lg font-bold text-white">{title}</h3>
    </div>

    <div className="p-5 space-y-4">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="flex items-center justify-between text-sm"
        >
          <div>
            <p className="font-semibold text-slate-700">{stat.label}</p>
            <p className="text-slate-500">{stat.count} transactions</p>
          </div>
          <p className="font-bold text-slate-800">
            {formatCurrency(stat.amount)}
          </p>
        </div>
      ))}
    </div>
  </div>
);


export default AdminDashboard;